import { useApolloClient } from '@apollo/client';
import { FlashbarProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import {
  GetUserinformationsDocument,
  GetUserinformationsQuery,
  Userinformation,
  useUpdateHolidayMutation,
} from '../../generated/graphql';
import { FormValues, schema } from './Create';
import Form, { selectOptions } from './Form';
import { createDDBItem } from './lib/Helper';

type EditProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedId?: string | null;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
};

export default function Edit({
  visible,
  selectedId,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
}: EditProps) {
  const client = useApolloClient();
  const [updateUserinformation, { loading: updateLoading }] =
    useUpdateHolidayMutation();
  const methods = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (selectedId && selectedId.length > 0) {
      const items = client.readQuery({
        query: GetUserinformationsDocument,
      }) as GetUserinformationsQuery;
      if (
        items &&
        items.listUserinformations &&
        items.listUserinformations.items &&
        items.listUserinformations.items.length > 0
      ) {
        const item = items.listUserinformations.items.filter(
          (item) => item?.id === selectedId,
        )[0];
        if (item) {
          const dayDiff = DateTime.fromSeconds(item.from ?? 0).diff(
            DateTime.fromSeconds(item.notifyStartDate ?? 0),
            'days',
          ).days;
          methods.reset({
            name: item.name ?? '',
            description: item.description ?? undefined,
            range: {
              startDate: DateTime.fromSeconds(item.from ?? 0).toFormat(
                'y-LL-dd',
              ),
              endDate: DateTime.fromSeconds(item.to ?? 0).toFormat('y-LL-dd'),
              type: 'absolute',
            },
            is_closed: item.is_closed ?? false,
            notifyUserOnWebsite: item.notifyUserOnWebsite ?? false,
            notifyStartDate: selectOptions.filter(
              (item) => item.value === `${dayDiff}`,
            )[0],
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, methods.reset, selectedId]);

  const onSubmit = (data: any) => {
    if (selectedId && selectedId.length > 0) {
      const entry = createDDBItem(data);
      const messageId = uuidv4();
      notifyInProgress({ id: messageId, header: 'Eintrag wird bearbeitet...' });
      updateUserinformation({
        variables: { input: { id: selectedId, ...entry } },
        update: (cache, { data }) => {
          const addData = data?.updateUserinformation;
          const existingData = cache.readQuery({
            query: GetUserinformationsDocument,
          }) as GetUserinformationsQuery;
          const oldData = existingData.listUserinformations
            ?.items as Userinformation[];
          cache.writeQuery({
            query: GetUserinformationsDocument,
            data: {
              listUserinformations: {
                items: oldData.map((item) => {
                  if (item.id === selectedId) {
                    return { ...addData };
                  } else {
                    return {
                      ...item,
                    };
                  }
                }),
              },
            },
          });
        },
      })
        .then((data) => {
          const result = data.data?.updateUserinformation;
          if (result) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              content: `Eintrag mit dem Namen "${result.name}" wurde bearbeitet.`,
              header: 'Gespeichert',
              dismissible: true,
            });
            setVisible(false);
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    }
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={
                  isEmpty(methods.formState.dirtyFields) || updateLoading
                }
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag bearbeiten'}
      >
        <Form isInUpdateState />
      </Modal>
    </FormProvider>
  );
}
